import { NumericTextBox } from '@progress/kendo-react-inputs'
import { MaterialWorking, LabourWorking } from '../../data/interfaces'
import './WorkingSections.css'

interface WorkingsAdjustmentSummaryProps {
  materialWorkings: MaterialWorking[]
  labourWorkings: LabourWorking[]
  /** Percentage uplift on the combined total. Null means no adjustment. */
  adjustmentPercent: number | null
  onAdjustmentPercentChange: (value: number | null) => void
  lockEdit?: boolean
}

/**
 * The Adjustment card and Total Summary that close out a workings tab.
 *
 * Shared by jobs, quotes and enquiries so the three cannot drift apart — they
 * had three separately written summaries before this, which is how quotes and
 * enquiries ended up without the adjustment at all.
 *
 * Totals are derived here from the workings themselves rather than accepted as
 * props, so every screen adds up the same way. Only the percentage belongs to
 * the parent, because only the parent knows where it is persisted.
 */
const WorkingsAdjustmentSummary = ({
  materialWorkings,
  labourWorkings,
  adjustmentPercent,
  onAdjustmentPercentChange,
  lockEdit,
}: WorkingsAdjustmentSummaryProps) => {
  const materialTotal = materialWorkings.reduce(
    (sum, item) => sum + (item.totalCost || item.total || 0),
    0
  )
  const labourTotal = labourWorkings.reduce(
    (sum, item) => sum + (item.totalCost || (item.hours || 0) * (item.rate || 0)),
    0
  )
  const baseTotal = materialTotal + labourTotal
  const adjustmentTotal = baseTotal * ((adjustmentPercent ?? 0) / 100)

  return (
    <>
      {/* Built to the same shape as a working section — a titled card with a
          dark blue total across the bottom — so it reads as part of the same
          list rather than a stray panel. */}
      <div className='working-section-card'>
        <div className='working-section-header'>
          <span className='working-section-title'>Adjustment</span>
        </div>
        <div className='working-section-body'>
          <div className='working-type-subtotal-row'>
            <span className='working-type-subtotal-label'>
              Material Total and Labour Total
            </span>
            <span className='working-type-subtotal'>£{baseTotal.toFixed(2)}</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
            }}
          >
            <span style={{ fontWeight: 600 }}>Adjustment (%)</span>
            <NumericTextBox
              value={adjustmentPercent}
              onChange={(e) => onAdjustmentPercentChange(e.value ?? null)}
              format='n2'
              step={0.5}
              width={140}
              disabled={lockEdit}
            />
          </div>
        </div>
        <div className='working-section-footer'>
          <span className='working-section-footer-label'>Adjustment Total</span>
          <span className='working-section-subtotal'>£{adjustmentTotal.toFixed(2)}</span>
        </div>
      </div>

      <div
        style={{
          marginTop: '2rem',
          backgroundColor: 'rgb(245, 249, 251)',
          borderRadius: '0.5rem',
          overflow: 'hidden',
        }}
      >
        <h4
          style={{
            margin: 0,
            padding: '0.75rem 1rem',
            backgroundColor: '#1e2a4e',
            color: '#ffffff',
          }}
        >
          Total Summary
        </h4>
        <div style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span>Material Total:</span>
            <span style={{ fontWeight: 'bold' }}>£{materialTotal.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span>Labour Total:</span>
            <span style={{ fontWeight: 'bold' }}>£{labourTotal.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span>Adjustment Total:</span>
            <span style={{ fontWeight: 'bold' }}>£{adjustmentTotal.toFixed(2)}</span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: '1px solid #ddd',
              paddingTop: '0.5rem',
              marginTop: '0.5rem',
            }}
          >
            <span style={{ fontWeight: 'bold' }}>Grand Total:</span>
            <span style={{ fontWeight: 'bold', color: '#007acc' }}>
              £{(baseTotal + adjustmentTotal).toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </>
  )
}

export default WorkingsAdjustmentSummary
